import { ChangeDetectionStrategy, Component, OnInit, OnDestroy, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { FirestoreProjectService } from '../../services/firestore-project.service';
import { AuthService } from '../../services/auth.service';
import { UserRole } from '../../models/user.model';
import { Project } from '../../models/project.model';
import { MobilePageHeaderComponent } from '../../components/mobile-page-header/mobile-page-header.component';
import { calculateExpectedProgress, isOverdueByDays } from '../../utils/progress.utils';

type FilterType = 'hm' | 'high-visibility' | 'delayed' | 'low-progress' | 'recurring';

interface FilterConfig {
    key: FilterType;
    label: string;
    icon: string;
    badgeBg: string;
    badgeText: string;
    headerBg: string;
    headerText: string;
    description: string;
}

@Component({
    selector: 'app-intervention-projects',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterModule, MobilePageHeaderComponent],
    templateUrl: './intervention-projects.component.html',
    styleUrls: ['./intervention-projects.component.css']
})
export class InterventionProjectsComponent implements OnInit, OnDestroy {
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private firestoreService = inject(FirestoreProjectService);
    private destroy$ = new Subject<void>();

    readonly filters: FilterConfig[] = [
        {
            key: 'hm',
            label: 'HM Projects',
            icon: 'fa-solid fa-crown',
            badgeBg: 'bg-blue-50 border-blue-300',
            badgeText: 'text-blue-700',
            headerBg: 'bg-blue-700',
            headerText: 'text-white',
            description: 'Projects with direct commitment from the Honourable OSD'
        },
        {
            key: 'high-visibility',
            label: 'High Visibility',
            icon: 'fa-solid fa-eye',
            badgeBg: 'bg-red-50 border-red-300',
            badgeText: 'text-red-700',
            headerBg: 'bg-red-700',
            headerText: 'text-white',
            description: 'Projects under high public / political visibility'
        },
        {
            key: 'delayed',
            label: 'Delayed > 60 Days',
            icon: 'fa-solid fa-clock',
            badgeBg: 'bg-orange-50 border-orange-300',
            badgeText: 'text-orange-700',
            headerBg: 'bg-orange-700',
            headerText: 'text-white',
            description: 'Projects that are stuck or overdue beyond 60 days'
        },
        {
            key: 'low-progress',
            label: 'Low Progress < 50%',
            icon: 'fa-solid fa-chart-line',
            badgeBg: 'bg-green-50 border-green-300',
            badgeText: 'text-green-700',
            headerBg: 'bg-green-700',
            headerText: 'text-white',
            description: 'Projects with physical progress below 50%'
        },
        {
            key: 'recurring',
            label: 'Recurring Intervention',
            icon: 'fa-solid fa-rotate',
            badgeBg: 'bg-purple-50 border-purple-300',
            badgeText: 'text-purple-700',
            headerBg: 'bg-purple-700',
            headerText: 'text-white',
            description: 'Projects that require recurring follow-up or intervention'
        }
    ];

    activeFilter = signal<FilterType>('hm');

    // ── Bulk select / delete ──────────────────────────────────────────────
    selectedIds           = signal<Set<string>>(new Set());
    showBulkDeleteConfirm = signal(false);
    isBulkDeleting        = signal(false);
    bulkDeleteSuccess     = signal<string | null>(null);

    isAllSelected = computed<boolean>(() => {
        const list = this.projects();
        return list.length > 0 && list.every(p => this.selectedIds().has(p.id));
    });

    private authService = inject(AuthService);

    readonly isMinisterOrOsd = this.authService.isMinisterOrOsd;

    private isMinisterRole = computed<boolean>(() =>
        this.authService.currentUser()?.role === UserRole.MINISTER
    );

    private rawProjects = signal<Project[]>([]);

    private allProjects = computed<Project[]>(() => {
        const projects = this.rawProjects();
        if (this.isMinisterRole()) {
            return projects.filter(p => !!p.hmPriority || !!p.recurringIntervention || !!(p as any).isDelayed);
        }
        return projects;
    });

    projects = computed<Project[]>(() => {
        const all = this.allProjects();
        switch (this.activeFilter()) {
            case 'hm':
                return all.filter(p => p.visibility === 'HM Committed' || !!p.hmPriority);
            case 'high-visibility':
                return all.filter(p => p.visibility === 'High Visibility' || p.visibility === 'HM Committed' || !!p.hmPriority || !!p.recurringIntervention);
            case 'delayed':
                return all.filter(p => {
                    if (p.status === 'Completed') return false;
                    if (!!p.isDelayed) return true;
                    if (p.status === 'Stuck') return true;
                    // past target date by > 60 days
                    if (p.end && isOverdueByDays(p.end, p.status, 60)) return true;
                    // significantly behind expected progress
                    if (p.start && p.end) {
                        const exp = calculateExpectedProgress(p.start, p.end);
                        if ((p.physical ?? 0) < exp - 15) return true;
                    }
                    return false;
                });
            case 'low-progress':
                return all.filter(p =>
                    p.status !== 'Completed' && (p.physical ?? 0) < 50
                );
            case 'recurring':
                return all.filter(p => !!p.recurringIntervention);
            default:
                return [];
        }
    });

    activeConfig = computed<FilterConfig>(() =>
        this.filters.find(f => f.key === this.activeFilter()) ?? this.filters[0]
    );

    // ── UI / View state ──────────────────────────────────────────────────
    searchQuery = signal('');
    cardView    = signal(true);
    currentPage = signal(1);
    readonly pageSize = 10;

    searchedProjects = computed<Project[]>(() => {
        const q = this.searchQuery().toLowerCase().trim();
        if (!q) return this.projects();
        return this.projects().filter(p =>
            p.name.toLowerCase().includes(q) ||
            (p.dept ?? '').toLowerCase().includes(q) ||
            (p.loc ?? '').toLowerCase().includes(q)
        );
    });

    totalPages        = computed(() => Math.max(1, Math.ceil(this.searchedProjects().length / this.pageSize)));
    pages             = computed(() => Array.from({ length: this.totalPages() }, (_, i) => i + 1));
    pageStart         = computed(() => this.searchedProjects().length === 0 ? 0 : (this.currentPage() - 1) * this.pageSize + 1);
    pageEnd           = computed(() => Math.min(this.currentPage() * this.pageSize, this.searchedProjects().length));
    paginatedProjects = computed(() => {
        const start = (this.currentPage() - 1) * this.pageSize;
        return this.searchedProjects().slice(start, start + this.pageSize);
    });

    ivStats = computed(() => {
        const p          = this.searchedProjects();
        const stuckCount = p.filter(x => x.status === 'Stuck').length;
        const avgProgress = p.length > 0
            ? Math.round(p.reduce((s, x) => s + (x.physical ?? 0), 0) / p.length)
            : 0;
        return { total: p.length, stuckCount, avgProgress };
    });

    ngOnInit(): void {
        // OSD/Minister always start on the recurring intervention view
        if (this.isMinisterOrOsd()) {
            this.activeFilter.set('recurring');
        }

        this.firestoreService.getProjects$()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: fsProjects => this.rawProjects.set(fsProjects),
                error: err => console.warn('[intervention-projects] Firestore error:', err)
            });

        this.route.queryParamMap
            .pipe(takeUntil(this.destroy$))
            .subscribe(params => {
                const f = params.get('filter') as FilterType | null;
                if (f && this.filters.some(x => x.key === f)) {
                    this.activeFilter.set(f);
                } else if (this.isMinisterOrOsd()) {
                    // No filter param — default OSD/Minister to recurring
                    this.activeFilter.set('recurring');
                }
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    goToPage(pg: number): void { this.currentPage.set(pg); }
    clearSearch(): void { this.searchQuery.set(''); this.currentPage.set(1); }

    getDeptAbbr(dept: string): string {
        const m: Record<string, string> = {
            'RD': 'RD', 'RWSS': 'RW', 'PR&DW': 'PR', 'Education': 'Ed',
            'Health': 'Ht', 'Irrigation': 'Ir', 'Housing': 'Ho',
            'Agriculture': 'Ag', 'Works': 'Wk', 'Energy': 'En',
        };
        return m[dept] ?? (dept ?? '??').substring(0, 2).toUpperCase();
    }

    getDeptColor(dept: string): string {
        const m: Record<string, string> = {
            'RD': '#2563eb', 'RWSS': '#0891b2', 'PR&DW': '#059669',
            'Education': '#7c3aed', 'Health': '#dc2626', 'Irrigation': '#0284c7',
            'Housing': '#d97706', 'Agriculture': '#16a34a', 'Works': '#ea580c', 'Energy': '#9333ea',
        };
        const fallbacks = ['#2563eb','#0891b2','#059669','#7c3aed','#dc2626','#d97706'];
        return m[dept] ?? fallbacks[(dept ?? '').charCodeAt(0) % fallbacks.length];
    }

    statusClass(s: string): string {
        const m: Record<string, string> = {
            'Completed': 'sc-done', 'In Progress': 'sc-ip', 'Stuck': 'sc-stuck', 'Planned': 'sc-plan'
        };
        return m[s] ?? 'sc-plan';
    }

    fmt(val: number): string {
        if (!val) return '₹0';
        if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)} Cr`;
        if (val >= 100000)   return `₹${(val / 100000).toFixed(2)} L`;
        return `₹${val.toLocaleString('en-IN')}`;
    }

    setFilter(key: FilterType): void {
        this.activeFilter.set(key);
        this.currentPage.set(1);
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { filter: key },
            queryParamsHandling: 'merge',
            replaceUrl: true   // filter changes must not stack in browser history
        });
    }

    openProject(id: string): void {
        this.router.navigate(['/dhamnagar-dashboard/project', id]);
    }

    getStatusClass(status: string): string {
        switch (status) {
            case 'Completed': return 'bg-emerald-100 text-emerald-800 border border-emerald-200';
            case 'In Progress': return 'bg-blue-100 text-blue-800 border border-blue-200';
            case 'Stuck': return 'bg-red-100 text-red-800 border border-red-200';
            case 'Planned': return 'bg-slate-100 text-slate-800 border border-slate-200';
            default: return 'bg-slate-100 text-slate-600';
        }
    }

    getProgressColor(physical: number): string {
        if (physical >= 75) return '#10b981';
        if (physical >= 50) return '#3b82f6';
        if (physical >= 25) return '#f97316';
        return '#ef4444';
    }

    goBack(): void {
        this.router.navigate(['/dhamnagar-dashboard/dashboard']);
    }

    // ── Bulk select / delete ──────────────────────────────────────────────

    toggleSelect(id: string): void {
        const s = new Set(this.selectedIds());
        if (s.has(id)) s.delete(id); else s.add(id);
        this.selectedIds.set(s);
    }

    toggleSelectAll(): void {
        const list = this.projects();
        this.selectedIds.set(
            this.isAllSelected() ? new Set() : new Set(list.map(p => p.id))
        );
    }

    clearSelection(): void {
        this.selectedIds.set(new Set());
        this.showBulkDeleteConfirm.set(false);
    }

    async bulkDeleteProjects(): Promise<void> {
        this.isBulkDeleting.set(true);
        const ids = [...this.selectedIds()].filter(id => !id.startsWith('static-'));
        try {
            await Promise.all(ids.map(id => this.firestoreService.deleteProject(id)));
            // Remove deleted projects from the raw signal; allProjects (computed) updates automatically
            this.rawProjects.update(list => list.filter(p => !this.selectedIds().has(p.id)));
            const count = this.selectedIds().size;
            this.selectedIds.set(new Set());
            this.showBulkDeleteConfirm.set(false);
            this.bulkDeleteSuccess.set(`${count} project${count > 1 ? 's' : ''} deleted successfully.`);
            setTimeout(() => this.bulkDeleteSuccess.set(null), 3500);
        } catch (err) {
            console.error('Bulk delete failed:', err);
        } finally {
            this.isBulkDeleting.set(false);
        }
    }

    protected readonly Math = Math;
}
